import { t } from '@lingui/core/macro';
import {
  MarkdownEditor,
  type MarkdownHeaderExtrasCtx,
  type WikiLinkTarget,
} from '@src/components/assets/editor/markdown/MarkdownEditor';
import type { ExtraSideTab } from '@src/components/milkdown-editor/EditorWithSidePanel';
import { EntityExecutionPanel } from '@src/components/entity-execution-panel';
import { useEntityByPath } from '@src/hooks/use-entity-by-path';
import { entityReloadKey } from '@src/utils/entity-reload-key';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { DockPointer } from '@src/navigation/DockPointer';
import { FSRef, ProcessKind, Skill } from '@sdk';
import { mainFileForType } from '@sdk/models/asset-editor';
import { cn } from '@src/lib/utils';
import { FlaskConical, History } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import { UsagePanel } from './UsagePanel';
import { PublishedToggle } from '@src/components/assets/editor/PublishedToggle';
import { useNestedHost } from '../nested-host';

interface SkillAssetEditorProps {
  /** FSRef to the skill folder. SKILL.md is resolved via child(). */
  fsRef: FSRef;
  /**
   * Pre-resolved skill entity. Passed by `<EntityResolutionGate>` from
   * `AssetEditorRouter`. When omitted (direct-mount callers), the editor
   * falls back to `useEntityByPath` for backwards compatibility.
   */
  skill?: Skill;
  /** Wiki page/namespace to retain for links when this asset is Wiki-rendered. */
  wikiLinkTarget?: WikiLinkTarget;
}

/**
 * Skill asset editor — the SKILL.md editor with its Chat + Backlinks side
 * window (keyed on the skill entity's typeId). The skill's other files are
 * browsed/created/deleted from the Assets sidebar, where the skill row expands
 * into its folder tree (see the `skillFolder` adapter) — there is no second
 * tree here.
 */
export function SkillAssetEditor({ fsRef, skill: providedSkill, wikiLinkTarget }: SkillAssetEditorProps) {
  const { entity: discoveredSkill } = useEntityByPath<Skill>(
    providedSkill ? null : Skill.type,
    providedSkill ? null : fsRef,
  );
  const skill = providedSkill ?? discoveredSkill;
  const { navigation } = useDockNavigation();

  // Everything below is keyed on the STABLE typeId string, never the `skill`
  // object — so a metadata-only `save()` (which hands back a new `skill` ref via
  // useEntity) does NOT rebuild the header/tabs/editorRef and remount the panels.
  // That remount loop is what turned one eval-toggle PUT into ~30 refetches.
  // The live entity is read through a ref so callbacks still act on the current
  // object without depending on its identity.
  const skillRef = useRef(skill);
  skillRef.current = skill;
  const skillKey = skill ? skill.typeId.toString() : null;

  // Body re-read token: SKILL.md re-reads when the skill entity's `updated_date`
  // advances (a reindex from an agent turn-end / invalidate re-parsed the folder).
  // Scalar so it's stable across identity-only skill ref churn; dirty-guarded.
  const reloadKey = entityReloadKey((skill as { updated_date?: unknown } | undefined)?.updated_date);

  // The URL-selected occurrence owns the bytes. An Entity may describe another
  // same-ID occurrence, so its primary doc must never replace this route ref.
  const mainFile = mainFileForType(Skill.type);
  const editorRef = useMemo(
    () => !mainFile || fsRef.path.endsWith(`/${mainFile}`) ? fsRef : fsRef.child(mainFile),
    [fsRef, mainFile],
  );

  const nested = useNestedHost();
  const onDelete = useCallback(async () => {
    const s = skillRef.current;
    if (!s) return;
    await s.delete();
    // Nested in another editor (a skill opened from the agent): back to it, not to the skill list.
    if (nested) nested.close();
    else navigation.openDock(DockPointer.forAssetList(Skill.type));
  }, [navigation, nested]);

  // One occurrence-scoped document patch; the backend refreshes the entity projection.
  const headerExtras = useCallback(({ fields, setField }: MarkdownHeaderExtrasCtx) => {
    const isEval = fields.eval === 'true';
    const toggle = () => setField('eval', !isEval);
    return (
      <button
        type="button"
        onClick={toggle}
        aria-pressed={isEval}
        title={isEval ? 'Under eval — click to stop evaluating' : 'Mark skill for eval'}
        data-testid="skill-eval-toggle"
        className={cn(
          'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors',
          isEval
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )}
      >
        <FlaskConical className="h-3.5 w-3.5" />
      </button>
    );
    // Stable identity: reads the live skill via `skillRef`, so it never rebuilds
    // on a skill ref change (only `fields`/`setField` from the editor drive it).
  }, []);

  // Skill eval history rides as an extra tab inside the editor's single side
  // drawer (Chat | Backlinks | Eval) rather than a second sibling rail. The
  // analysis processes are launched elsewhere (in-trace Evaluate button, the
  // tab-close adapter) keyed to this skill's TypeId; `EntityExecutionPanel`
  // auto-lists by `target_typeid_str`. Memoized on `skill` so the array
  // identity is stable across editor keystrokes (else MarkdownEditor's tab/
  // panel memos rebuild every render).
  // Keyed on the STABLE skillKey/editorRef only (never the `skill` object) so a
  // metadata refetch handing back a new `skill` ref does NOT rebuild this array
  // and remount the panels — that remount would wipe UsagePanel's scanned state.
  // The panel reads the live skill via `skillRef`, snapshotted at first build.
  const extraSideTabs = useMemo<ExtraSideTab[] | undefined>(() => {
    if (!skillKey || !skillRef.current) return undefined;
    return [
      {
        id: 'usage',
        label: t`Usage`,
        icon: History,
        description: t`Sessions that used this skill — analyze, improve, commit`,
        panel: <UsagePanel skill={skillRef.current} skillFile={editorRef} />,
      },
      {
        id: 'eval',
        label: t`Eval`,
        icon: FlaskConical,
        description: t`Skill evaluations`,
        panel: (
          <EntityExecutionPanel
            target={skillKey}
            processType={ProcessKind.Execution}
            headerLabel="Skill eval"
            className="min-h-0 flex-1"
          />
        ),
      },
    ];
    // Rebuild for a new identity or selected occurrence so Usage keeps the
    // route-selected file even when two copies share an entity ID.
  }, [skillKey, editorRef]);

  return (
    <MarkdownEditor
      fsRef={editorRef}
      editEntity={skill}
      headerLeading={skill ? <PublishedToggle entity={skill} /> : null}
      chatTarget={skillKey}
      headerExtras={headerExtras}
      extraSideTabs={extraSideTabs}
      onDelete={skillKey ? onDelete : undefined}
      deleteLabel={skill?.name ?? undefined}
      reloadKey={reloadKey}
      wikiLinkTarget={wikiLinkTarget}
    />
  );
}
